import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Icon, LazyImg, Button } from 'common/ui'
import dayjs from 'dayjs'
import { useCallback } from 'react'
import { makeDAppInfoUrl, makeDAppEditUrl } from '../utils'
import { useRouter } from 'next/router'
import { DIOX } from 'common/api'
import clss from 'classnames'

const DAppCardStyled = styled.div`
  width: 426px;
  height: 249px;
  background: #1a1919;
  border: 1px solid #3a3939;
  border-radius: 12px;
  padding: 0 24px;
  cursor: pointer;
  &:hover {
    border-color: #e0c286;
  }
  .card-header {
    height: 80px;
    display: flex;
    align-items: center;
    padding: 20px 0;
    position: relative;
    &:after {
      content: '';
      width: 100%;
      height: 1px;
      background: #3a3939;
      position: absolute;
      bottom: -0.5px;
      left: 0;
    }
    img {
      margin-right: 12px;
      width: 40px;
      height: 40px;
      border-radius: 100%;
    }
    .title {
      flex: 1;
      .name {
        font-weight: 500;
        font-size: 15px;
        line-height: 20px;
        color: #ffffff;
        margin-bottom: 4px;
      }
      .id {
        font-weight: 400;
        font-size: 13px;
        line-height: 15px;
        color: #a3a3a3;
      }
    }
    .edit {
      width: 88px;
      height: 32px;
      font-size: 15px;
      font-weight: 500;
    }
    .right-text {
      font-weight: 400;
      font-size: 15px;
      line-height: 20px;
      &.failed {
        color: #ec404e;
      }
      &.pending {
        color: #f78433;
      }
      &.confirmed {
        color: #00cc88;
      }
    }
  }
  .base-group {
    margin-top: 40px;
    .base-item.date {
      width: 214px;
      margin: 0;
      padding-left: 15px;
    }
    .base-item + .base-item {
      padding-left: 0;
      margin-right: 0;
    }
    .val {
      word-break: break-word;
    }
  }
`
type DAppCardProps = {
  data: DIOX.DAppSummary
}
const DAppCard = ({ data }: DAppCardProps) => {
  const router = useRouter()

  const isFinalized = useMemo(() => {
    return data?.TxnStatus === 'Finalized'
  }, [data])

  const renderRight = useMemo(() => {
    if (data.ID && isFinalized) {
      const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        e.preventDefault()
        router.push(makeDAppEditUrl(data.Address))
      }
      return (
        <Button className="edit" type="primary" onClick={handleEdit}>
          Edit
        </Button>
      )
    }
    if (isFinalized) {
      return ''
    }
    return (
      <span className={clss(data?.TxnStatus?.toLowerCase(), 'right-text')}>
        {data.TxnStatus}
      </span>
    )
  }, [data, isFinalized])

  const handleCardClick = useCallback(
    (e) => {
      if (data.ID && data.Address && isFinalized) {
        router.push(makeDAppInfoUrl(data.Address))
      }
    },
    [data, router, isFinalized]
  )

  return (
    <DAppCardStyled onClick={handleCardClick}>
      <div className="card-header">
        <LazyImg
          url={data.IconUrl}
          defaultUrl="/img/dapp-default.png"
          alt="dapp-logo"
        />
        <div className="title">
          <p className="name">{data.Symbol}</p>
          <p className="id"># {(isFinalized && data.ID) || '--'}</p>
        </div>
        {renderRight}
      </div>
      <div className="base-group">
        <div className="base-item date">
          <Icon className="icon" type="a-createdate" />
          <span className="val">
            {dayjs(data.BlockTime).format('MM/DD/YY HH:mm')}
          </span>
          <p className="desc">Creation Date</p>
        </div>
        <div className="base-item">
          <Icon className="icon" type="contract" />
          <span className="val line-clamp">{data.Contracts || 0}</span>
          <p className="desc">Contracts</p>
        </div>
      </div>
    </DAppCardStyled>
  )
}
export default DAppCard
